import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { TableChart } from "@mui/icons-material"; // Excel tablosuna benzer bir ikon
import * as XLSX from "xlsx";
import api from "../../api";

// Araçları (Utils) import ediyoruz
import {
  calculateAkkClass,
  getAkkDepthCode,
  getAkkTextureAndPerm,
  akkFormulu,
  indeksFormulu,
  akkFormulParcalari,
} from "../../utils/akkUtils";
import { numarayaGoreSirala, dosyaAdi } from "../../utils/exportUtils";
import {
  FIZYOGRAFYA_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
} from "../../constants/fieldConstants";
import { TEKSTUR_OPTIONS } from "../../constants/horizonConstants";
import { calculateStorieIndex } from "../../utils/storieUtils";
import { calculateLegend, getDepthCode } from "../../utils/legendUtils";

export default function JobExcelExportButton({ jobId, jobName }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      // 1. Backend'den çalışmaya ait tüm profilleri çek
      const res = await api.get(`/admin/job-profiles/${jobId}`);

      if (!res.data.success || res.data.data.length === 0) {
        alert("Bu çalışmaya ait kayıtlı profil bulunamadı.");
        setLoading(false);
        return;
      }

      // Profiller KENDI numaralarina gore kucukten buyuge (P1, P2 ... P100).
      const profiles = numarayaGoreSirala(res.data.data, "profil_no");
      const excelRows = []; // Tüm profillerin satırlarını burada toplayacağız

      // 2. Her profil için hesaplamaları yap ve satırı oluştur
      profiles.forEach((profile) => {
        let d = {};
        try {
          d =
            typeof profile.full_data === "string"
              ? JSON.parse(profile.full_data)
              : profile.full_data;
        } catch (e) {
          return; // Hatalı veriyi atla
        }
        console.log(d);
        const akk = calculateAkkClass(d);
        const storie = calculateStorieIndex(d);
        const legend = calculateLegend(d);

        const horizons = d.horizons || [];
        const firstHor = horizons[0] || {};

        const takenSamples = horizons.filter((h) => !h.ornek_alinamadi);
        const sampleDepthsString =
          takenSamples.length > 0
            ? `${takenSamples.length}N:${takenSamples.map((h) => `(${h.derinlik_min}-${h.derinlik_max || "?"})`).join("")}`
            : "-";

        const horizonSequence = horizons
          .map((h) => h.sembol)
          .filter((s) => s)
          .join("-");

        let targetHor = null;
        if (horizons.length === 1) targetHor = horizons[0];
        else if (horizons.length === 2) targetHor = horizons[1];
        else if (horizons.length > 2) targetHor = horizons[horizons.length - 2];

        const permInfo = getAkkTextureAndPerm(
          targetHor?.tekstur,
          targetHor?.kirec,
        );
        const permVal = permInfo.val || "";

        // Ust / alt toprak tekstur sutunlari formulle AYNI kaynaktan gelir
        // (akkFormulParcalari). Cok sig toprakta (derinlik 5) alt toprak yoktur,
        // "att" bos kalir.
        const parcalar = akkFormulParcalari(d, akk.formulaPermeability);
        const topTexCodeStr = parcalar.ustHarf;
        const botTexCodeStr = parcalar.altHarf;

        const rawSlopeCode = legend.slopeCode.trim();
        const slopeLetterOnly = rawSlopeCode.charAt(0) || "";
        const slopeNumberOnly = rawSlopeCode.substring(1);

        // Mevcut kodundaki eroNumber (1, 2, 3 veya 4 gelir)
        const eroNumber = d.su_erozyon_derecesi?.charAt(0) || "1";

        // Sınıfa göre DERECE (X faktörü) eşleştirmesi
        const eroFaktorMap = {
          1: "100",
          2: "90",
          3: "80",
          4: "50",
        };

        const eroFaktoru = eroFaktorMap[eroNumber] || "100"; // Varsayılan 100

        const depthAkk = getAkkDepthCode(d.tesirli_derinlik);

        let akkDrain = "";
        let drenajDerece = "";
        if (d.tabii_drenaj) {
          const drText = d.tabii_drenaj;
          if (drText.includes("yok") || drText.includes("(İ)")) {
            akkDrain = "";
            drenajDerece = "100";
          } else if (drText.includes("Sulamada") || drText.includes("(K)")) {
            akkDrain = "K";
            drenajDerece = "85";
          } else if (
            drText.includes("F1") ||
            drText.includes("F2") ||
            drText.includes("F3")
          ) {
            // Derinliklere göre F1, F2, F3 ayrımı
            if (drText.includes("F1")) {
              akkDrain = "F1";
              drenajDerece = "65";
            } else if (drText.includes("F2")) {
              akkDrain = "F2";
              drenajDerece = "50";
            } else if (
              drText.includes("F3") ||
              drText.toLowerCase().includes("göllenme")
            ) {
              akkDrain = "F3";
              drenajDerece = "25";
            }
          }
        }

        // Mevcut kodundaki tuzCode'u kullanıyoruz (1, 2, 3 veya 4 gelir)
        const tuzCode = d.tuzluluk
          ? d.tuzluluk.match(/\d+/)
            ? d.tuzluluk.match(/\d+/)[0]
            : "1"
          : "";

        // SN numarasına göre sağ taraftaki X faktörü eşleştirmesi
        const tuzFaktorMap = {
          1: "100",
          2: "80", // Hafif Tuzlu aralığının alt sınırı
          3: "50", // Orta Tuzlu aralığının alt sınırı
          4: "50", // Şiddetli Tuzlu
        };

        const tuzFaktoru = tuzFaktorMap[tuzCode] || "";

        // Mevcut alkCode mantığını X4'ü kapsayacak şekilde genişletiyoruz
        const alkCode = d.alkalilik
          ? d.alkalilik.match(/X\d+/)
            ? d.alkalilik.match(/X\d+/)[0]
            : d.alkalilik.includes("Çok Fazla") || d.alkalilik.includes("X4")
              ? "X4"
              : d.alkalilik.includes("Orta") || d.alkalilik.includes("X2")
                ? "X2"
                : d.alkalilik.includes("Fazla") || d.alkalilik.includes("X3")
                  ? "X3"
                  : "X1"
          : "";

        // Senin belirttiğin özel değerlere göre eşleştirme
        const alkFaktorMap = {
          X1: "100",
          X2: "80",
          X3: "65",
          X4: "30", // İstediğin gibi X4'ü 30 olarak set ettik
        };

        const alkFaktoru = alkFaktorMap[alkCode] || "";

        // Mevcut kodundaki borCode'u kullanıyoruz (B1, B2, B3 veya B4 gelir)
        const borCode = d.bor
          ? d.bor.includes("B")
            ? d.bor.match(/B\d+/)?.[0] || "B1"
            : "B1"
          : "";

        // Sınıfa göre DERECE (X faktörü) eşleştirmesi
        const borFaktorMap = {
          B1: "100",
          B2: "80", // Tablodaki 90-80 aralığının alt sınırı
          B3: "60", // Tablodaki 80-60 aralığının alt sınırı
          B4: "50",
        };

        const borFaktoru = borFaktorMap[borCode] || "";

        // Kayalılık sembolünü ayıkla (r0, r1, r2, r3, r4)
        const kayaCode = d.kayalilik
          ? d.kayalilik.match(/r\d/i)
            ? d.kayalilik.match(/r\d/i)[0].toLowerCase()
            : d.kayalilik.includes("Az")
              ? "r0"
              : ""
          : "";

        // Sınıfa göre DERECE eşleştirmesi
        // r1 ve r2 için tablodaki ağırlıklı/alt değerler seçilmiştir
        const kayaFaktorMap = {
          r0: "95",
          r1: "70", // 95-65 aralığı için temsilci değer (veya 10% karşılığı)
          r2: "50", // 60-50 aralığı için temsilci değer
          r3: "50",
          r4: "50",
        };

        const kayaFaktoru = kayaFaktorMap[kayaCode] || "100"; // Kayalık yoksa 100 sayılır

        const getLabelByCode = (code) => {
          const option = FIZYOGRAFYA_OPTIONS.find((item) => item.code === code);
          return option ? option.label : "Kod bulunamadı";
        };
        const getTeksturAciklama = (kod) => {
          if (!kod) return "";

          // 1. Listede bu kodla başlayan öğeyi bul (Örn: "SICL" -> "SICL (Siltli Killi Tın)")
          const tamMetin = TEKSTUR_OPTIONS.find((option) =>
            option.startsWith(kod + " "),
          );

          if (tamMetin) {
            // 2. Parantez içindeki kısmı ayıkla
            return tamMetin.split("(")[1].split(")")[0];
          }

          return kod; // Eğer listede bulamazsa harfin kendisini geri döndürür
        };

        const getErozyonDereceMetni = (dereceNo) => {
          if (!dereceNo) return "";

          // 1. Gelen değeri string'e çevir (Eğer sayı gelirse hata vermemesi için)
          const no = dereceNo.toString().trim();

          // 2. Listede bu numarayla başlayan öğeyi bul
          const bulundu = SU_EROZYON_DERECESI_OPTIONS.find((opt) =>
            opt.startsWith(no),
          );

          if (bulundu) {
            // 3. "3 - Şiddetli (...)" formatından sadece "Şiddetli" kısmını ayıkla
            let metin = bulundu.split("-")[1].split("(")[0];
            return metin.trim();
          }

          return dereceNo; // Bulamazsa gelen rakamı geri döndür
        };

        // TUZLULUK Dönüştürücü
        const getTuzlulukAciklama = (val) => {
          const s = String(val).trim();
          if (s === "1") return "TUZSUZ";
          if (s === "2") return "HAFİF TUZLU";
          if (s === "3") return "ORTA TUZLU";
          if (s === "4") return "ŞİDDETLİ TUZLU";
          return s; // Eğer zaten metin gelirse bozmaz
        };

        // ALKALİLİK Dönüştürücü
        const getAlkalilikAciklama = (val) => {
          const s = String(val).toUpperCase().trim();
          // Hem X1, X2 kodlarına hem de 1, 2 sayılarına göre kontrol
          if (s === "X1" || s === "1") return "HAFİF";
          if (s === "X2" || s === "2") return "ORTA";
          if (s === "X3" || s === "3") return "FAZLA";
          if (s === "X4" || s === "4") return "ÇOK FAZLA";
          return s;
        };

        const getBorAciklama = (val) => {
          const s = String(val).toUpperCase().trim();

          // B1, B2 gibi kodlar veya doğrudan 1, 2 sayıları gelirse:
          if (s === "B1" || s === "1") return "Borsuz";
          if (s === "B2" || s === "2") return "Hafif Borlu";
          if (s === "B3" || s === "3") return "Orta Borlu";
          if (s === "B4" || s === "4") return "Yüksek Borlu";

          return s; // Eşleşme olmazsa orijinal değeri döndürür
        };

        const getAraziKullanimMetni = (fullText) => {
          if (!fullText) return "";

          // Eğer içinde parantez varsa, paranteze göre böl ve ilk (sol) parçayı al
          // "Kuru Tarım Arazisi (K)" -> "Kuru Tarım Arazisi "
          let metin = fullText.includes("(")
            ? fullText.split("(")[0]
            : fullText;

          // Sondaki gereksiz boşluğu temizle
          return metin.trim();
        };

        const getDrenajDurumuMetni = (fullText) => {
          if (!fullText) return "";

          // Parantez içindeki kodu yakala (İ, K, F1, F2 veya F3)
          const codeMatch = fullText.match(/\(([^)]+)\)/);
          const code = codeMatch ? codeMatch[1] : "";

          if (code === "İ") return "İyi";
          if (code === "K") return "Yararlı";
          if (code.startsWith("F")) return "Fena"; // F1, F2 ve F3'ün hepsini "Fena" yapar

          return fullText; // Eşleşme yoksa orijinali döner
        };

        const getSymbolOnly = (val) => {
          if (!val) return "";
          if (val.includes("-")) return val.split("-")[0].trim();
          const parenthesisMatch = val.match(/\(([^)]+)\)$/);
          if (parenthesisMatch) {
            if (parenthesisMatch[1].length > 3) return val.split(" ")[0].trim();
            return parenthesisMatch[1].trim();
          }
          return val.split(" ")[0].trim();
        };

        const cleanPhase = (val) => {
          if (!val) return "";
          const lower = val.toLowerCase();
          if (
            lower.includes("yok") ||
            lower.includes("taşsız") ||
            lower.includes("r0")
          )
            return "";
          return val.split("-")[0].trim();
        };

        const getSafeString = (val) => (val ? val : "");

        const formatNumber = (num) => {
          if (num === undefined || num === null || num === "") return "";
          const parsed = parseFloat(num);
          return isNaN(parsed) ? "" : parsed.toFixed(2);
        };

        // AKK ve indeks formulu BUTUN ciktilarda ayni: ortak ureticiden gelir.
        const akkFormulStr = akkFormulu(d);
        const cleanNdxFormulStr = indeksFormulu(d);

        // Her bir profili array'e satır olarak ekliyoruz
        excelRows.push({
          profil_no: profile.profil_no || "",
          profilgrup: d.profilgrup || "",
          derinlik: depthAkk,
          utt: topTexCodeStr,
          att: botTexCodeStr,
          // Alt toprak yoksa (cok sig, derinlik 5) gecirgenligi de yazilmaz.
          alttprgec: botTexCodeStr ? permVal : "",
          tuz: tuzCode > 1 ? tuzCode : "",
          alkalilik: alkCode !== "X1" ? alkCode : "",
          bor: borCode !== "B1" ? borCode : "",
          drenaj: akkDrain,
          taslilik: cleanPhase(d.taslilik),
          Çakıl: cleanPhase(d.cakillilik),
          kayalilik: cleanPhase(d.kayalilik),
          kuldurum: getSymbolOnly(d.arazi_kullanim_sinif),
          egim: slopeNumberOnly,
          egimsinif: slopeLetterOnly,
          erozyon: eroNumber,
          ndx_formul: cleanNdxFormulStr,
          a_degeri: formatNumber(storie.factorA),
          b_degeri: formatNumber(storie.factorB),
          c_degeri: formatNumber(storie.factorC),
          drenaj_d: drenajDerece,
          tuz_d: tuzFaktoru,
          alkalid: alkFaktoru,
          bor_d: borFaktoru,
          erozyon_d: eroFaktoru,
          kayalik_d: kayaFaktoru,
          x_diger: formatNumber(storie.factorX),
          deger_topl: formatNumber(
            storie.factorA * storie.factorB * storie.factorC * storie.factorX,
          ),
          index: formatNumber(storie.finalIndex),
          index_ack: `A=${formatNumber(storie.factorA)}, B=${formatNumber(storie.factorB)}, C=${formatNumber(storie.factorC)}, X=${formatNumber(storie.factorX)}, (I=${formatNumber(storie.finalIndex)})`,
          akk_formul: akkFormulStr,
          akk_sinifi: akk.finalClass,
          alttahdit: akk.subClassString,
          x: formatNumber(d.koordinat_x),
          y: formatNumber(d.koordinat_y),
          z: formatNumber(d.yukseklik),
          kururenk:
            `${firstHor.renk_sinif_kuru || ""} ${firstHor.renk_kod_kuru || ""}`.trim(),
          yasrenk:
            `${firstHor.renk_sinif_yas || ""} ${firstHor.renk_kod_yas || ""}`.trim(),
          btg: getSafeString(d.btg),
          amateryal: getSafeString(d.ana_materyal),
          fzyografya: getSafeString(d.fizyografya),
          utt_ack: getTeksturAciklama(firstHor.tekstur),
          // "att" ile ayni katman: ikinci katman (yoksa ilki); cok sigda bos.
          att_ack: botTexCodeStr
            ? getTeksturAciklama((horizons[1] || firstHor).tekstur)
            : "",
          pozsyn_ack: getSafeString(getLabelByCode(d.fizyografya)),
          erzyn_ack: getErozyonDereceMetni(d.su_erozyon_derecesi),
          derinlikcm: formatNumber(Math.round(Number(d.tesirli_derinlik))),
          tslk_ack: getSafeString(d.taslilik),
          tuz_ack: getTuzlulukAciklama(getSafeString(d.tuzluluk)),
          alk_ack: getAlkalilikAciklama(getSafeString(d.alkalilik)),
          yuzde_egim: getSafeString(d.egim_yuzde),
          kylk_ack: getSafeString(d.kayalilik),
          bor_ack: getBorAciklama(getSafeString(d.bor)),
          arz_klnm: getAraziKullanimMetni(
            getSafeString(d.arazi_kullanim_sinif),
          ),
          drnj_ack: getDrenajDurumuMetni(getSafeString(d.tabii_drenaj)),
          urun: getSafeString(d.urun),
          alnornsay: sampleDepthsString,
          hrzn_dizil: horizonSequence || "",
          SERI_KOD: getSafeString(d.seri_kodu),
          SERI_ADI: getSafeString(d.toprak_serisi),
          // excelRows.push içinde:
          SERI_LEJAN:
            `${legend.leftCode} [(${legend.topString}) / (${legend.bottomString || "-"})] ${legend.rightCode}`
              .replace(/\s+/g, " ")
              .trim(),
          ARAZI_TIPI: getSafeString(d.arazi_kullanim_detay),
          ARAZI_FORM: "",
          AraziSekli: getSafeString(d.mikrorolyef),
          ORT_EGIM: "",
        });
      });

      // 3. EXCEL OLUŞTUR VE İNDİR
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);

      const narrowCols = [
        "profilgrup",
        "derinlik",
        "utt",
        "att",
        "alttprgec",
        "tuz",
        "alkalilik",
        "bor",
        "drenaj",
        "taslilik",
        "Çakıl",
        "kuldurum",
        "kayalilik",
        "egim",
        "egimsinif",
        "erozyon",
        "a_degeri",
        "b_degeri",
        "c_degeri",
        "x_diger",
        "x",
        "y",
        "z",
      ];

      const wscols = Object.keys(excelRows[0]).map((key) => {
        if (narrowCols.includes(key)) return { wch: 6 };
        if (key === "alnornsay" || key === "ndx_formul") return { wch: 30 };
        if (
          key.includes("_ack") ||
          key.includes("formul") ||
          key.includes("ADI") ||
          key.includes("LEJAN")
        )
          return { wch: 25 };
        return { wch: 12 };
      });

      ws["!cols"] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, "Tüm_Profiller");
      XLSX.writeFile(wb, dosyaAdi(jobName, "T Şablon Profiller", "xlsx"));
    } catch (error) {
      console.error(error);
      alert("Toplu Excel oluşturulurken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Bu çalışmadaki TÜM profilleri tek bir T-Şablon Excel tablosunda indirir">
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton
          color="success"
          onClick={handleExport}
          disabled={loading}
          sx={{
            bgcolor: "rgba(46, 125, 50, 0.05)",
            mb: 0.5,
            "&:hover": { bgcolor: "rgba(46, 125, 50, 0.15)" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="success" />
          ) : (
            <TableChart fontSize="small" />
          )}
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.6rem",
            color: "text.secondary",
            fontWeight: "bold",
          }}
        >
          T Şablon
        </Typography>
      </Box>
    </Tooltip>
  );
}
